/**
 * Waiting list admin view — list active entries, with a Remove action
 * per row. Operator removal sends a courtesy email to the client.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
} from '@bookaburra/ui';
import { AlertTriangle, ListChecks } from 'lucide-react';
import { useDocumentTitle } from '../lib/use-document-title';
import { fetchBookingTenant } from '../booking-flow/api';
import { PlanGate } from '../components/PlanGate';

export const Route = createFileRoute('/admin/$slug/waitlist')({
  component: WaitlistPage,
});

interface WaitlistEntry {
  id: string;
  serviceId: string;
  serviceName: string;
  dateLocal: string;
  name: string;
  email: string | null;
  phone: string | null;
  smsOptIn: boolean;
  preferredLocale: string;
  createdAt: number;
}

async function fetchWaitlist(slug: string): Promise<{ entries: WaitlistEntry[] }> {
  const r = await fetch(`/api/admin/${encodeURIComponent(slug)}/waitlist`);
  if (!r.ok) throw new Error(`error ${r.status}`);
  return r.json();
}

async function removeEntry(slug: string, entryId: string): Promise<void> {
  const r = await fetch(
    `/api/admin/${encodeURIComponent(slug)}/waitlist/${encodeURIComponent(entryId)}`,
    { method: 'DELETE' },
  );
  if (!r.ok) {
    const body = (await r.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `error ${r.status}`);
  }
}

function WaitlistPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const tenantBrand = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantBrand.data
      ? `Waiting list · ${tenantBrand.data.tenant.name} Admin`
      : 'Waiting list',
  );

  const listQ = useQuery({
    queryKey: ['admin-waitlist', slug],
    queryFn: () => fetchWaitlist(slug),
  });

  const removeMut = useMutation({
    mutationFn: (entryId: string) => removeEntry(slug, entryId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['admin-waitlist', slug] }),
  });

  // Group entries by date for easier scanning.
  const grouped = new Map<string, WaitlistEntry[]>();
  for (const e of listQ.data?.entries ?? []) {
    const arr = grouped.get(e.dateLocal) ?? [];
    arr.push(e);
    grouped.set(e.dateLocal, arr);
  }
  const dates = [...grouped.keys()].sort();

  return (
    <main className="mx-auto max-w-3xl px-6 py-10 sm:px-12">
      <header className="mb-8">
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight">
          <ListChecks className="h-7 w-7 text-muted-foreground" />
          <span>Waiting list</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          People hoping for a slot. They're auto-notified when a slot opens
          on their chosen day.
        </p>
      </header>

      <PlanGate slug={slug} requiredPlan="small-business">
      {listQ.isLoading ? (
        <div className="h-40 animate-pulse rounded-2xl bg-secondary" />
      ) : listQ.error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Couldn&rsquo;t load the waiting list</AlertTitle>
          <AlertDescription>{(listQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : dates.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          No one is on the waiting list right now.
        </Card>
      ) : (
        <div className="space-y-6">
          {dates.map((date) => (
            <section key={date}>
              <h2 className="mb-3 text-sm font-semibold uppercase tracking-widest text-muted-foreground">
                {formatDateHeading(date)} · {grouped.get(date)!.length} on list
              </h2>
              <div className="space-y-3">
                {grouped.get(date)!.map((e) => (
                  <Card key={e.id} className="p-4 flex items-start justify-between gap-3">
                    <div className="text-sm">
                      <p className="font-medium">{e.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {e.serviceName}
                      </p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {e.email ?? '—'}
                        {e.phone ? ` · ${e.phone}` : ''}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMut.mutate(e.id)}
                      loading={removeMut.isPending && removeMut.variables === e.id}
                      disabled={removeMut.isPending}
                    >
                      Remove
                    </Button>
                  </Card>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      </PlanGate>
    </main>
  );
}

function formatDateHeading(dateIso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateIso);
  if (!m) return dateIso;
  const [_, y, mo, d] = m;
  const date = new Date(Date.UTC(Number(y), Number(mo) - 1, Number(d)));
  return new Intl.DateTimeFormat('en-AU', {
    timeZone: 'UTC',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
}
